/**
 * While a video is being made: what is happening now, what is done, how long it has taken against how long it
 * usually takes, and what was heard from the prompt — silent (nothing plays until it is ready). Replaces the looping
 * storyboard preview (Milo, 2026-10-08: "7 minutes, I couldn't tell if it was moving or stuck"; the audio looped).
 */
import { useEffect, useState } from 'react';
import { Bell, BellRing, Check, Loader2, RotateCcw, TriangleAlert } from 'lucide-react';
import type { VideoMakingStage, VideoProject } from '@/cowork/omniad/api/coworkApi';

const LABEL: Record<VideoMakingStage, (clips: number) => { title: string; detail: string }> = {
  watch: (n) => ({ title: n ? `Watching your ${n} clip${n > 1 ? 's' : ''}` : 'Reading your material', detail: 'What is said, where the product shows, the strongest moments' }),
  write: () => ({ title: 'Writing the video', detail: 'Scenes, lines and cuts, in the brand’s voice' }),
  check: () => ({ title: 'Checking claims and your asks', detail: 'Every claim against the product facts; your lines kept' }),
  sound: () => ({ title: 'Cutting the sound', detail: 'Whole-sentence cuts, the music bed, the mix' }),
  place: () => ({ title: 'Placing text and captions', detail: 'Clear of faces, the product and the creator’s own captions' }),
  review: () => ({ title: 'Looking at every frame', detail: 'Hierarchy, crowding and collisions, fixed before it is called ready' }),
};

/** About twice what a step usually takes. */
const SLOW_MS: Record<VideoMakingStage, number> = { watch: 150_000, write: 150_000, check: 60_000, sound: 150_000, place: 90_000, review: 150_000 };

const mmss = (ms: number) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

/** How long it usually takes (measured on real runs): clips are watched and the frames looked at. */
function usual(p: VideoProject): string {
  const kind = p.making?.kind || (p.status === 'drafting' ? 'draft' : 'revise');
  if (kind === 'review') return 'usually about a minute';
  if (kind === 'revise') return 'usually 1–3 minutes';
  const clips = p.media.filter((m) => m.kind === 'video').length;
  // Measured: about 2 min with one clip already watched; new clips are watched first, a minute or more each.
  return clips ? `usually ${clips > 3 ? '5–8' : clips > 1 ? '3–6' : '2–4'} minutes with ${clips} clip${clips > 1 ? 's' : ''}` : 'usually 1–3 minutes';
}

export function MakingPanel({ project, compact }: { project: VideoProject; compact?: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const [notify, setNotify] = useState(() => { try { return typeof Notification !== 'undefined' && Notification.permission === 'granted'; } catch { return false; } });
  const m = project.making;
  const clips = project.media.filter((x) => x.kind === 'video').length;
  const stages: VideoMakingStage[] = m?.stages || (project.status === 'drafting' ? ['watch', 'write', 'check', 'sound', 'place', 'review'] : ['write', 'check', 'sound', 'place']);
  const current = m?.stage || stages[0];
  const done = new Set(m?.done || []);
  const started = Date.parse(m?.startedAt || project.updatedAt);
  // A step running past its usual time (measured on real runs) says so; a silent task is shown as interrupted by the API.
  const stepFor = m?.stageAt ? now - Date.parse(m.stageAt) : 0;
  const slow = stepFor > (SLOW_MS[current] || 120_000);
  const step = stages.indexOf(current) + 1;
  const headline = m?.kind === 'review' ? 'Looking at the frames' : project.status === 'drafting' ? 'Making your video' : 'Making your change';

  if (compact) {
    return (
      <div className="pointer-events-none absolute inset-x-3 top-3 flex items-center gap-2 rounded-lg bg-black/70 px-3 py-2 text-[12.5px] text-white" role="status" aria-live="polite">
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[#a9f600]" />
        <span className="truncate"><b>{headline}</b> · {LABEL[current](clips).title} (step {step} of {stages.length}) · {mmss(now - started)}</span>
      </div>
    );
  }

  const ask = async () => {
    try { if (typeof Notification === 'undefined') return; const r = await Notification.requestPermission(); setNotify(r === 'granted'); } catch { /* blocked */ }
  };

  return (
    <div className="flex h-full w-full items-center justify-center overflow-y-auto p-4">
      <div className="w-full max-w-[520px] rounded-2xl border border-[#eaecf0] bg-white p-5 shadow-[0_12px_32px_rgba(16,24,40,0.10)]" role="status" aria-live="polite">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-[17px] font-semibold text-[#101828]">{headline}</h2>
          <span className="font-mono text-[13px] tabular-nums text-[#475467]">{mmss(now - started)}</span>
        </div>
        <p className="mt-0.5 text-[12.5px] text-[#667085]">Step {step} of {stages.length} · {usual(project)}. Nothing plays until it is ready.</p>

        <ol className="mt-4 flex flex-col gap-2.5">
          {stages.map((s) => {
            const { title, detail } = LABEL[s](clips);
            const state = done.has(s) ? 'done' : s === current ? 'doing' : 'todo';
            return (
              <li key={s} className="flex items-start gap-2.5">
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${state === 'done' ? 'bg-[#a9f600] text-[#2e024c]' : state === 'doing' ? 'bg-[#2e024c] text-[#a9f600]' : 'border border-[#d0d5dd] bg-white'}`}>
                  {state === 'done' ? <Check className="h-3 w-3" strokeWidth={3} /> : state === 'doing' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                </span>
                <span className="min-w-0">
                  <span className={`block text-[13.5px] ${state === 'todo' ? 'text-[#98a2b3]' : 'font-semibold text-[#101828]'}`}>{title}</span>
                  {state === 'doing' && <span className="block text-[12px] text-[#667085]">{detail}</span>}
                </span>
              </li>
            );
          })}
        </ol>
        {slow && <p className="mt-3 text-[12px] text-[#b54708]">Still working — this step is taking longer than usual ({mmss(stepFor)}).</p>}

        {(m?.asks?.length || project.angle) && (
          <div className="mt-4 rounded-xl bg-[#f9fafb] px-3.5 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#98a2b3]">{m?.asks?.length ? 'What we heard you ask for' : 'Your brief'}</div>
            {m?.asks?.length
              ? <ul className="mt-1.5 flex flex-col gap-1 text-[13px] text-[#344054]">{m.asks.map((a) => <li key={a}>{a}</li>)}</ul>
              : <p className="mt-1.5 line-clamp-3 text-[13px] text-[#344054]">“{project.angle}”</p>}
          </div>
        )}

        {project.media.length > 0 && (
          <div className="mt-4 flex gap-1.5 overflow-x-auto">
            {project.media.slice(0, 8).map((x, i) => (
              <div key={`${x.url}-${i}`} className="h-16 w-11 shrink-0 overflow-hidden rounded-md bg-[#101828]">
                {(x.posterUrl || x.kind === 'image') && <img src={x.posterUrl || x.url} alt="" className="h-full w-full object-cover opacity-80" />}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[#f2f4f7] pt-3 text-[12.5px] text-[#667085]">
          <span>You can leave this page; it keeps going.</span>
          {typeof Notification !== 'undefined' && (notify
            ? <span className="inline-flex items-center gap-1.5 text-[#027a48]"><BellRing className="h-3.5 w-3.5" />We’ll notify you</span>
            : <button type="button" onClick={() => void ask()} className="inline-flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] px-2.5 py-1 font-semibold text-[#344054] hover:bg-[#f9fafb]"><Bell className="h-3.5 w-3.5" />Notify me when it’s ready</button>)}
        </div>
      </div>
    </div>
  );
}

/** A draft that could not be made (or was interrupted): what happened and one way forward. */
export function MakingFailed({ project, onRetry, busy }: { project: VideoProject; onRetry: () => void; busy: boolean }) {
  return (
    <div className="flex h-full w-full items-center justify-center p-4">
      <div className="w-full max-w-[460px] rounded-2xl border border-[#fecdca] bg-white p-5 shadow-[0_12px_32px_rgba(16,24,40,0.10)]" role="alert">
        <div className="flex items-center gap-2 text-[16px] font-semibold text-[#b42318]"><TriangleAlert className="h-4 w-4" />This video wasn’t made</div>
        <p className="mt-1.5 text-[13px] text-[#475467]">{project.error || 'Something went wrong while making it.'}</p>
        {project.angle && <p className="mt-3 line-clamp-3 rounded-lg bg-[#f9fafb] px-3 py-2 text-[12.5px] text-[#344054]">“{project.angle}”</p>}
        <button type="button" disabled={busy} onClick={onRetry} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#2e024c] px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 text-[#a9f600]" />}Try again with the same brief
        </button>
      </div>
    </div>
  );
}

/** Once it is ready: each ask from the prompt, on screen or not (a missed one says so instead of hiding). */
export function AsksStrip({ checks, onClose }: { checks: Array<{ ask: string; kept: boolean }>; onClose: () => void }) {
  if (!checks.length) return null;
  const missed = checks.filter((c) => !c.kept).length;
  return (
    <div className="flex w-full flex-wrap items-center gap-1.5 rounded-xl border border-[#eaecf0] bg-white px-3 py-2 text-[12px]">
      <span className="font-semibold text-[#344054]">{missed ? `${missed} of your asks ${missed > 1 ? 'are' : 'is'} not on screen` : 'Everything you asked for is in'}</span>
      {checks.map((c) => (
        <span key={c.ask} title={c.kept ? 'On screen' : 'Not on screen — ask Lykke to add it'} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${c.kept ? 'bg-[#ecfdf3] text-[#027a48]' : 'bg-[#fffaeb] text-[#b54708]'}`}>
          {c.kept ? <Check className="h-3 w-3" strokeWidth={3} /> : <TriangleAlert className="h-3 w-3" />}{c.ask}
        </span>
      ))}
      <button type="button" onClick={onClose} className="ml-auto text-[#98a2b3] hover:text-[#475467]" aria-label="Hide">×</button>
    </div>
  );
}
